<template>
  <div class="details-wrapper">
    <AdvancePaymentSummary v-if="permissions.advance && detailActive && advanceTripNumber" :trip-number="advanceTripNumber" @resolved="$emit('advanceCompleted')" />
    <div v-if="manualStatusActive" class="manual-status-banner" :title="manualStatusInfo">
      🧭 Ручной статус — автосинхронизация отключена<span v-if="manualStatusInfo">{{ manualStatusInfo }}</span>
    </div>
    <section class="section actions-section" v-if="actionButtons.length || canManageManualStatus">
      <h4 class="section-title">Действия</h4>
      <div class="operation-actions">
        <button
          v-for="button in actionButtons"
          :key="`${button.operation}-${button.label}`"
          class="operation-btn"
          :class="button.className"
          :disabled="!hasTelegramAccount || actionInProgress"
          @click.stop="runOperation(button)"
        >
          {{ actionInProgress ? 'Выполняется...' : button.label }}
        </button>
        <button
          v-if="canManageManualStatus"
          class="operation-btn operation-manual"
          :disabled="!hasTelegramAccount || actionInProgress"
          @click.stop="openManualStatusModal"
        >
          Изменить статус вручную
        </button>
      </div>
      <div v-if="!hasTelegramAccount" class="action-hint">
        Привяжите Telegram в настройках пользователя, чтобы выполнять операции.
      </div>

      <CloseOrderPanel
        v-if="canClosePanel && detailActive"
        :order="order"
        :permissions="permissions"
        :action-in-progress="actionInProgress"
        :fetch-subsidy-table="subsidyFetcher"
        @close="runOperation"
        @request-approval="onRequestApproval"
      />
    </section>

    <section class="section">
      <h4 class="section-title">Транспортировка</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in transportationRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">Маршрут</h4>
      <div class="points" v-if="points.length">
        <div :class="['point-card', pt.cardClass]" v-for="(pt, i) in points" :key="i">
          <div class="point-header">
            <span :class="['point-index', pt.loading ? 'point-loading' : 'point-unloading']">{{ i + 1 }}</span>
            <span class="point-city">
              {{ pt.title }}
              <span v-if="pt.operationLabel" class="point-op">({{ pt.operationLabel }})</span>
            </span>
            <span v-if="pt.badge" :class="['point-badge', pt.badgeClass]" :title="pt.registryTitle || ''">{{ pt.badge }}</span>
          </div>
          <div v-if="pt.address" class="point-address">{{ pt.address }}</div>
          <div class="grid pt-grid">
            <div class="row copy-row" v-for="r in pt.rows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
              <span class="row-label">{{ r.label }}</span>
              <span class="row-value">{{ r.value }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty">Нет данных по точкам маршрута.</div>
    </section>

    <section class="section">
      <div class="section-header">
        <h4 class="section-title">Тариф</h4>
        <div class="tariff-actions">
          <button
            v-if="canEditCost"
            class="comment-toggle"
            :disabled="!hasTelegramAccount || actionInProgress"
            @click="showCostModal = true"
          >✎ Редактировать цену</button>
          <button
            v-if="canEditBaseRate"
            class="comment-toggle"
            @click="showBaseRateModal = true"
          >⚡ Ставка направления</button>
        </div>
      </div>
      <div class="grid">
        <div class="row copy-row" v-for="r in tariffRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">Транспорт и Груз</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in transportCargoRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">Менеджер</h4>
      <div class="grid">
        <div class="row copy-row" v-for="r in managerRows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
          <span class="row-label">{{ r.label }}</span>
          <span class="row-value">{{ r.value }}</span>
        </div>
      </div>
    </section>

    <section class="section">
      <h4 class="section-title">История действий</h4>
      <div class="history-list" v-if="leftQueueRows.length">
        <div class="history-card" v-for="(item, i) in leftQueueRows" :key="i">
          <div class="history-title">{{ i + 1 }}. {{ item.title }}</div>
          <div class="grid">
            <div class="row copy-row" v-for="r in item.rows" :key="r.label" :title="copyTitle(r.value)" @click="copyValue(r.value)">
              <span class="row-label">{{ r.label }}</span>
              <span class="row-value">{{ r.value }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty">Нет данных по вышедшим из очереди.</div>
    </section>

    <section class="section" v-if="comment && comment !== '—'">
      <div class="section-header">
        <h4 class="section-title">Комментарий</h4>
        <button class="comment-toggle" @click="isCommentOpen = !isCommentOpen">
          {{ isCommentOpen ? 'Скрыть' : 'Показать' }}
        </button>
      </div>
      <div v-if="isCommentOpen" class="comment-text">{{ comment }}</div>
    </section>

    <div v-if="manualStatusModalOpen" class="modal-overlay" @click.self="manualStatusModalOpen = false">
      <div class="manual-status-modal">
        <h4 class="modal-title">🧭 Ручное изменение статуса</h4>
        <p class="modal-tip">Автосинхронизация статуса будет отключена.</p>
        <label class="modal-field">
          <span class="modal-label">Новый статус</span>
          <select v-model="manualStatusValue">
            <option v-for="opt in manualStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </label>
        <label class="modal-field">
          <span class="modal-label">Причина (необязательно)</span>
          <textarea v-model="manualStatusReason" rows="3"></textarea>
        </label>
        <div class="modal-actions">
          <button class="operation-btn operation-manual" :disabled="actionInProgress" @click="applyManualStatus">Применить</button>
          <button v-if="order.manual_status_override" class="operation-btn operation-secondary" :disabled="actionInProgress" @click="resetManualStatusOverride">🔄 Вернуть автосинхронизацию</button>
          <button class="operation-btn" :disabled="actionInProgress" @click="manualStatusModalOpen = false">Отмена</button>
        </div>
      </div>
    </div>

    <ManualCostEditModal
      v-model:show="showCostModal"
      :order="order"
      :is-admin="isCostAdmin"
      :loading="actionInProgress"
      @apply="applyManualCost"
      @reset="removeManualCost"
    />

    <ApproveCreateModal
      v-model:show="showApproveModal"
      :order="order"
      :request="approvalRequest"
      :loading="actionInProgress"
      @apply="applyApproval"
    />

    <BaseRateInlineModal
      v-model:show="showBaseRateModal"
      :order="order"
      @saved="onBaseRateSaved"
    />
  </div>
</template>

<script>
import { createToaster } from '@meforma/vue-toaster';
import ManualCostEditModal from '@/pages/distribution/components/ManualCostEditModal.vue';
import BaseRateInlineModal from '@/pages/distribution/components/BaseRateInlineModal.vue';
import CloseOrderPanel from '@/pages/distribution/components/CloseOrderPanel.vue';
import ApproveCreateModal from '@/pages/distribution/components/ApproveCreateModal.vue';
import { fetchSubsidyTable } from '@/backend/distributionApi';
import AdvancePaymentSummary from './AdvancePaymentSummary.vue';
import {
  pointTitle,
  pointAddress,
  pointRegistryCategory,
  pointRegistryName,
  pointBadgeLabel,
} from '@/pages/distribution/pointInfo.mjs';

const toaster = createToaster();

const MANUAL_STATUS_OPTIONS = [
  { value: 1, label: '🪖 Открыт' },
  { value: 2, label: '🔥 Закрыт' },
  { value: 4, label: '🚔 Отменен' },
  { value: 8, label: '♻️ Удален' },
  { value: 16, label: '🕸 Устарел' },
  { value: 32, label: '💥 Срыв' },
  { value: 64, label: '🚫 Отказ' },
];

export default {
  name: 'DistributionDetails',
  components: { ManualCostEditModal, BaseRateInlineModal, CloseOrderPanel, ApproveCreateModal, AdvancePaymentSummary },
  props: {
    order: { type: Object, required: true },
    detailActive: { type: Boolean, default: true },
    currentUser: { type: Object, default: () => ({}) },
    permissions: { type: Object, default: () => ({}) },
    operationLoadingUid: { type: String, default: null },
    runOperationMutation: { type: Function, required: true },
  },
  emits: ['baseRateSaved', 'advanceCompleted'],
  data() {
    return {
      isCommentOpen: false,
      manualStatusModalOpen: false,
      manualStatusValue: 1,
      manualStatusReason: '',
      showCostModal: false,
      showBaseRateModal: false,
      showApproveModal: false,
      approvalRequest: null,
      subsidyFetcher: fetchSubsidyTable,
    };
  },
  computed: {
    advanceTripNumber() {
      return String(this.order.distr_id || this.order.order?.meta?.trip_id || '').trim();
    },
    hasTelegramAccount() {
      return Boolean(this.currentTgUsername);
    },
    actionUid() {
      return this.order?.order?.uid || this.order?.order?.meta?.uid;
    },
    actionInProgress() {
      return this.operationLoadingUid === this.actionUid;
    },
    currentTgUsername() {
      return this.normalizeTelegramUsername(this.currentUser.tg_username);
    },
    isOpened() {
      return Number(this.order.status) === 1;
    },
    isClosed() {
      return Number(this.order.status) === 2;
    },
    isCanceled() {
      return Number(this.order.status) === 4;
    },
    isManuallyCanceled() {
      return Boolean(this.order.manually_canceled_by || (this.order.manual_cancellation_history || []).length);
    },
    canManageManualStatus() {
      return Boolean(this.permissions.manualStatus);
    },
    canEditCost() {
      return Boolean(
        this.permissions.write || this.permissions.admin,
      );
    },
    isCostAdmin() {
      return Boolean(
        this.permissions.admin || this.permissions.approvals,
      );
    },
    canEditBaseRate() {
      return Boolean(this.permissions.baseRates);
    },
    manualStatusActive() {
      return Boolean(this.order.manual_status_override);
    },
    manualStatusOptions() {
      return MANUAL_STATUS_OPTIONS;
    },
    manualStatusInfo() {
      if (!this.manualStatusActive) return '';
      const by = this.userName(this.order.manual_status_changed_by);
      const at = this.date(this.order.manual_status_changed_at);
      const reason = this.order.manual_status_reason ? ` — ${this.order.manual_status_reason}` : '';
      return ` (${by}, ${at}${reason})`;
    },
    isPending() {
      return this.isCurrentTelegramUser(this.order.pending);
    },
    isWorker() {
      return this.isCurrentTelegramUser(this.order.worker);
    },
    isInternalBooking() {
      return this.isCurrentTelegramUser(this.order.internal_booking);
    },
    isInQueue() {
      return (this.order.pending_queue || []).some((user) => this.isCurrentTelegramUser(user));
    },
    actionButtons() {
      if (!this.actionUid) return [];

      if (this.isClosed) {
        return [
          { operation: 'reopenClosedOrder', label: 'Вернуть в открытые', className: 'operation-secondary', confirm: 'Вернуть рейс в открытые?' },
          { operation: 'finallyCancelClosedOrder', label: 'Отменить рейс', className: 'operation-danger', confirm: 'Окончательно отменить закрытый рейс?' },
        ];
      }

      if (this.isCanceled) {
        return this.isManuallyCanceled
          ? [{ operation: 'reopenCanceledOrder', label: 'Переоткрыть отмененный', className: 'operation-secondary', confirm: 'Вернуть отмененный рейс в работу?' }]
          : [];
      }

      if (!this.isOpened) return [];

      const actions = [];

      if (this.isWorker) {
        actions.push(
          { operation: 'prolongWork', label: 'Продлить работу', className: 'operation-secondary' },
          { operation: 'releaseWork', label: 'Освободить работу', className: 'operation-danger' },
        );
      } else if (this.isInternalBooking) {
        actions.push({ operation: 'releaseBooking', label: 'Снять бронь ТС', className: 'operation-danger' });
      } else if (this.isPending) {
        actions.push(
          { operation: 'takeWork', label: 'В работу', className: 'operation-primary' },
          { operation: 'bookInternal', label: 'Бронь под ТС', className: 'operation-secondary' },
          { operation: 'leaveQueue', label: 'Покинуть очередь', className: 'operation-danger' },
        );
      } else if (this.isInQueue) {
        if (!this.order.internal_booking) {
          actions.unshift({ operation: 'bookInternalPriority', label: 'Бронь под ТС (приоритет)', className: 'operation-secondary' });
        }
        actions.push({ operation: 'leaveQueue', label: 'Покинуть очередь', className: 'operation-danger' });
      } else {
        actions.push({ operation: 'joinQueue', label: 'В очередь', className: 'operation-primary' });
      }

      if (this.isCostAdmin && this.order.approved_result) {
        actions.push({
          operation: 'deleteApproval',
          label: '🗑 Удалить согласование',
          className: 'operation-danger',
          confirm: 'Удалить согласование тарифа?',
        });
      }

      return actions;
    },
    canClosePanel() {
      return this.isOpened && (this.isWorker || this.isInternalBooking);
    },
    orderData() {
      return this.order.order || {};
    },
    transportationRows() {
      const meta = this.orderData.meta || {};
      const externalNumber = meta.external_id && meta.service_name
        ? `${meta.external_id}:${meta.service_name}`
        : null;
      return [
        { label: 'Номер заявки', value: this.v(this.order.distr_id) },
        { label: 'Номер платформы', value: this.v(meta.number || meta.external_id) },
        { label: 'Внешний ключ', value: this.v(externalNumber) },
        { label: 'Статус', value: this.statusLabel() },
        { label: 'Платформа', value: this.v(String(this.order.display_name || meta.service_name || '')) },
        { label: 'Клиент', value: this.v(meta.client_name) },
        { label: 'Trip ID', value: this.v(meta.trip_id) },
      ];
    },
    points() {
      const registry = this.order.points_registry;
      return (this.orderData.route?.points || []).map((pt, i) => {
        const category = pointRegistryCategory(registry, i);
        const badge = pointBadgeLabel(category);
        const registryName = pointRegistryName(registry, i);
        return {
          title: pointTitle(pt) || this.v(pt.city_name),
          address: pointAddress(pt),
          badge,
          badgeClass: category === 'top' ? 'point-badge-top' : category === 'key' ? 'point-badge-key' : null,
          cardClass: category === 'top' ? 'point-card-top' : category === 'key' ? 'point-card-key' : null,
          registryTitle: badge && registryName ? `Клиент: ${registryName}` : null,
          operationLabel: pt.operation_type === 'loading' ? 'Погрузка' : pt.operation_type === 'unloading' ? 'Разгрузка' : null,
          loading: pt.operation_type !== 'unloading',
          rows: [
            { label: 'Регион', value: this.v(pt.region_name) },
            { label: 'Индекс', value: this.v(pt.index) },
            { label: 'Плановое время', value: this.dateTimeTz(pt) },
          ],
        };
      });
    },
    tariffRows() {
      const meta = this.tariffMeta();
      const approved = this.order.approved_result;
      const type = this.isClosed && this.order.closing_type
        ? this.order.closing_type
        : approved?.new_cost_without_nds != null || approved?.new_cost_with_nds != null
        ? 'Согласованный'
        : (this.order.manual_cost != null || this.order.manual_cost_nds != null)
          ? 'Ручной'
          : (this.order.embedded_route?.cost != null || this.order.embedded_route?.cost_nds != null)
            ? 'Базовый'
            : 'Пусто';
      const rows = [
        { label: 'Тип тарифа', value: type },
        { label: 'Текущий без/с НДС', value: `${this.money(meta.left)} / ${this.money(meta.right)}` },
        { label: 'Базовый без/с НДС', value: `${this.money(meta.embeddedLeft)} / ${this.money(meta.embeddedRight)}` },
        { label: 'Ручной без/с НДС', value: `${this.money(meta.manualLeft)} / ${this.money(meta.manualRight)}` },
        { label: 'Согласованный без/с НДС', value: `${this.money(approved?.new_cost_without_nds)} / ${this.money(approved?.new_cost_with_nds)}` },
        { label: 'Новая дата', value: this.date(approved?.new_date) },
        { label: 'Оплата', value: this.v(approved?.new_pay) },
      ];
      if (this.isClosed) {
        const factCost = typeof this.order.fact_cost === 'number'
          ? `${this.money(this.order.fact_cost)} ${this.order.fact_cost_nds ? 'с НДС' : 'без НДС'}`
          : '—';
        rows.push(
          { label: 'Тип закрытия', value: this.v(this.order.closing_type) },
          { label: 'Факт стоимость', value: factCost },
          { label: 'Срок оплаты (факт)', value: this.v(this.order.fact_payment_term) },
          { label: 'Скидка (факт)', value: this.order.fact_reduction_percent != null ? `−${this.order.fact_reduction_percent}%` : '—' },
        );
      }
      return rows;
    },
    transportCargoRows() {
      const veh = this.orderData.veh || {};
      const cargo = this.orderData.cargo || {};
      return [
        { label: 'Тип ТС', value: this.v(veh.type) },
        { label: 'Тонаж', value: this.num(veh.tonnage, 'т') },
        { label: 'Тип груза', value: this.v(cargo.list?.join(', ')) },
        { label: 'Вес', value: this.num(cargo.weight, 'кг') },
        { label: 'Объём', value: this.num(cargo.volume, 'м3') },
        { label: 'Количество мест', value: this.num(cargo.pallet) },
        { label: 'Опасный', value: cargo.hazardous ? 'Да' : 'Нет' },
      ];
    },
    managerRows() {
      const app = this.order.approved_result;
      const queue = (this.order.pending_queue || []).map((u) => this.userName(u)).filter((n) => n !== '—').join(', ');
      return [
        { label: 'В работе', value: this.userName(this.order.worker) },
        { label: 'В поиске', value: this.userName(this.order.pending) },
        { label: 'Бронь под ТС', value: this.userName(this.order.internal_booking) },
        { label: 'Очередь', value: this.v(queue) },
        { label: 'Ответственный за тариф', value: this.v(app?.answer_by_name) },
        { label: 'Дата ответа', value: this.date(app?.answer_dt) },
        { label: 'Тариф подтвержден', value: app?.confirmed === true ? 'Да' : app?.confirmed === false ? 'Нет' : '—' },
      ];
    },
    leftQueueRows() {
      return (this.order.left_queue || []).map((user) => ({
        title: this.userName(user),
        rows: [
          { label: 'Вошел', value: this.date(user.joined_dt) },
          { label: 'Вышел', value: this.date(user.left_dt) },
          { label: 'Причина', value: this.leftReason(user.left_reason) },
          { label: 'Запрос', value: user.requested_min ? `${user.requested_min} мин` : '—' },
          { label: 'Истекло', value: user.expired ? 'Да' : 'Нет' },
        ],
      }));
    },
    comment() {
      return this.v(this.orderData.info?.comment);
    },
  },
  methods: {
    normalizeTelegramUsername(value) {
      const username = String(value || '').trim().replace(/^@+/, '').toLowerCase();
      return username ? `@${username}` : '';
    },
    isCurrentTelegramUser(user) {
      if (!user) return false;
      const userName = this.normalizeTelegramUsername(user.username);
      return Boolean(userName && this.currentTgUsername && userName === this.currentTgUsername);
    },
    v(val) {
      if (val === null || val === undefined || val === '') return '—';
      return String(val);
    },
    money(val) {
      if (typeof val !== 'number') return '—';
      return val.toLocaleString('ru-RU');
    },
    num(val, suffix) {
      if (typeof val !== 'number') return '—';
      const rounded = Number.isInteger(val) ? String(val) : val.toFixed(2);
      return `${rounded}${suffix ? ' ' + suffix : ''}`;
    },
    date(val) {
      if (!val) return '—';
      return new Date(val).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    dateTimeTz(pt) {
      if (!pt.planed_date && !pt.timezone) return '—';
      const parts = [];
      if (pt.planed_date) parts.push(this.date(pt.planed_date));
      if (pt.timezone) parts.push(this.utcOffset(pt.planed_date, pt.timezone));
      return parts.join(' · ');
    },
    utcOffset(date, timezone) {
      if (!date || !timezone) return timezone || '—';
      try {
        const part = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          timeZoneName: 'shortOffset',
        }).formatToParts(new Date(date)).find((item) => item.type === 'timeZoneName')?.value;
        return part ? part.replace('GMT', 'UTC') : timezone;
      } catch {
        return timezone;
      }
    },
    statusLabel() {
      const s = Number(this.order.status);
      if (s === 1) return 'Открыт';
      if (s === 2) return 'Закрыт';
      if (s === 4) return 'Отмена';
      if (s === 8) return 'Удален';
      if (s === 16) return 'Устарел';
      if (s === 32) return 'Срыв';
      if (s === 64) return 'Отказ';
      return this.v(s);
    },
    userName(user) {
      if (!user) return '—';
      const username = user.username ? `@${String(user.username).trim().replace(/^@+/, '')}` : null;
      return [user.first_name, user.last_name, username]
        .filter(Boolean)
        .join(' ') || '—';
    },
    leftReason(reason) {
      if (!reason) return '—';
      const map = { expired: 'Истекло время', timeout: 'Истекло время', pending_timeout: 'Истекло время', cancel: 'Отменено', cancelled: 'Отменено', canceled: 'Отменено', manual: 'Снято вручную', leave: 'Покинул очередь', left: 'Покинул очередь', exit: 'Покинул очередь', worker: 'Перешел в работу', worker_taken: 'Перешел в работу', blocked: 'Заблокирован', closed: 'Закрыт', opened: 'Открыт' };
      return map[reason.trim().toLowerCase()] || reason;
    },
    copyTitle(value) {
      return value && value !== '—' ? 'Скопировать' : '';
    },
    copyValue(value) {
      if (!value || value === '—') return;
      navigator.clipboard?.writeText(String(value)).then(() => {
        toaster.success('Скопировано');
      }).catch(() => {});
    },
    async runOperation(buttonOrOperation) {
      if (!this.hasTelegramAccount) {
        toaster.error('Telegram не привязан');
        return;
      }
      const button = typeof buttonOrOperation === 'string' ? { operation: buttonOrOperation } : buttonOrOperation;
      let payload = button.payload ? { ...button.payload } : undefined;
      if (button.confirm && !window.confirm(button.confirm)) return;
      if (['reopenClosedOrder', 'finallyCancelClosedOrder', 'reopenWithContextDisagreement'].includes(button.operation)) {
        const comment = window.prompt('Причина отмены / переоткрытия рейса:');
        if (comment === null) return;
        payload = { comment: comment.trim() };
      }
      try {
        const result = await this.runOperationMutation({
          uid: this.actionUid,
          operation: button.operation,
          payload,
        });
        if (result?.success === false) {
          if (payload?.advance) window.dispatchEvent(new Event('advance-updated'));
          toaster.error(result.message || 'Операция не выполнена');
          return;
        }
        toaster.success(result?.message || 'Операция выполнена');
        window.dispatchEvent(new Event('advance-updated'));
      } catch (error) {
        if (payload?.advance) window.dispatchEvent(new Event('advance-updated'));
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message;
        toaster.error(message || 'Ошибка операции');
      }
    },
    onRequestApproval(request) {
      this.approvalRequest = request;
      this.showApproveModal = true;
    },
    async applyApproval(payload) {
      if (!this.hasTelegramAccount) {
        toaster.error('Telegram не привязан');
        return;
      }
      try {
        const result = await this.runOperationMutation({
          uid: this.actionUid,
          operation: 'createApproval',
          payload,
        });
        if (result?.success === false) {
          toaster.error(result.message || 'Операция не выполнена');
          return;
        }
        toaster.success(result?.message || 'Запрос на согласование отправлен');
        this.showApproveModal = false;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message;
        toaster.error(message || 'Ошибка операции');
      }
    },
    tariffMeta() {
      const o = this.order;
      const er = o.embedded_route;
      const ap = o.approved_result;

      const hasApproved = ap?.new_cost_without_nds != null || ap?.new_cost_with_nds != null;

      const manualLeft = o.manual_cost != null ? o.manual_cost : null;
      const manualRight = o.manual_cost_nds != null ? o.manual_cost_nds : null;
      const embeddedLeft = er?.cost != null ? er.cost : null;
      const embeddedRight = er?.cost_nds != null ? er.cost_nds : null;
      const hasFileCost = embeddedLeft !== null || embeddedRight !== null;
      const hasManualCost = manualLeft !== null || manualRight !== null;

      return {
        left: hasManualCost ? manualLeft : hasFileCost ? embeddedLeft : null,
        right: hasManualCost ? manualRight : hasFileCost ? embeddedRight : null,
        manualLeft,
        manualRight,
        embeddedLeft,
        embeddedRight,
      };
    },
    openManualStatusModal() {
      this.manualStatusValue = Number(this.order.status) || 1;
      this.manualStatusReason = this.order.manual_status_reason || '';
      this.manualStatusModalOpen = true;
    },
    async applyManualStatus() {
      if (!this.hasTelegramAccount) {
        toaster.error('Telegram не привязан');
        return;
      }
      const status = Number(this.manualStatusValue);
      if (!Number.isFinite(status)) {
        toaster.error('Выберите статус');
        return;
      }
      const payload = { status };
      const reason = (this.manualStatusReason || '').trim();
      if (reason) payload.reason = reason;
      try {
        const result = await this.runOperationMutation({
          uid: this.actionUid,
          operation: 'setManualStatus',
          payload,
        });
        if (result?.success === false) {
          toaster.error(result.message || 'Операция не выполнена');
          return;
        }
        toaster.success(result?.message || 'Статус изменен');
        window.dispatchEvent(new Event('advance-updated'));
        this.manualStatusModalOpen = false;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message;
        toaster.error(message || 'Ошибка операции');
      }
    },
    async resetManualStatusOverride() {
      if (!this.hasTelegramAccount) {
        toaster.error('Telegram не привязан');
        return;
      }
      try {
        const result = await this.runOperationMutation({
          uid: this.actionUid,
          operation: 'resetManualStatus',
        });
        if (result?.success === false) {
          toaster.error(result.message || 'Операция не выполнена');
          return;
        }
        toaster.success(result?.message || 'Автосинхронизация включена');
        this.manualStatusModalOpen = false;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message;
        toaster.error(message || 'Ошибка операции');
      }
    },
    async applyManualCost(payload) {
      if (!this.hasTelegramAccount) {
        toaster.error('Telegram не привязан');
        return;
      }
      try {
        const result = await this.runOperationMutation({
          uid: this.actionUid,
          operation: 'setManualCost',
          payload,
        });
        if (result?.success === false) {
          toaster.error(result.message || 'Операция не выполнена');
          return;
        }
        toaster.success(result?.message || 'Цена обновлена');
        this.showCostModal = false;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message;
        toaster.error(message || 'Ошибка операции');
      }
    },
    async removeManualCost() {
      if (!this.hasTelegramAccount) {
        toaster.error('Telegram не привязан');
        return;
      }
      try {
        const result = await this.runOperationMutation({
          uid: this.actionUid,
          operation: 'resetManualCost',
        });
        if (result?.success === false) {
          toaster.error(result.message || 'Операция не выполнена');
          return;
        }
        toaster.success(result?.message || 'Ручная цена сброшена');
        this.showCostModal = false;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : detail?.detail || detail?.message || error?.message;
        toaster.error(message || 'Ошибка операции');
      }
    },
    onBaseRateSaved() {
      this.$emit('baseRateSaved');
    },
  },
};
</script>

<style scoped>
.details-wrapper {
  padding: 10px 12px;
  font-size: 14px;
  color: #333;
  background: transparent;
}

.header {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 4px 0 8px;
  border-bottom: 1px solid #e5e7eb;
  background: transparent;
}

.subtitle {
  font-size: 12px;
  color: #666;
  text-align: left;
}

.dot {
  margin: 0 6px;
}

.section {
  margin-bottom: 10px;
  padding: 0 0 10px;
  border-bottom: 1px solid #e5e7eb;
  background: transparent;
}

.actions-section {
  padding-bottom: 12px;
}

.operation-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.operation-btn {
  padding: 6px 10px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.operation-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.operation-primary {
  background: #e8f0fe;
  border-color: #9ec5fe;
  color: #084298;
}

.operation-secondary {
  background: #fff8df;
  border-color: #ffe08a;
  color: #7a5b00;
}

.operation-danger {
  background: #fff0f0;
  border-color: #f5b5b5;
  color: #b83235;
}

.operation-manual {
  background: #fff3cd;
  border-color: #ffe08a;
  color: #7a5b00;
}

.manual-status-banner {
  margin-bottom: 10px;
  padding: 7px 10px;
  border: 1px solid #ffe08a;
  border-radius: 6px;
  background: #fff8df;
  color: #7a5b00;
  font-size: 13px;
  font-weight: 600;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.manual-status-modal {
  width: 360px;
  max-width: 90vw;
  padding: 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.modal-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.modal-tip {
  margin: 0 0 12px;
  font-size: 13px;
  color: #666;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.modal-label {
  font-size: 13px;
  color: #555;
  font-weight: 600;
}

.manual-status-modal select,
.manual-status-modal textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
}

.manual-actions,
.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.action-hint {
  margin-top: 8px;
  color: #7a5b00;
  font-size: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 10px;
  padding-bottom: 0;
  border-bottom: 0;
  color: #007bff;
  text-align: left;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.section-header .section-title {
  margin: 0;
}

.tariff-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.comment-toggle {
  padding: 2px 8px;
  border: 1px solid #d7dce2;
  border-radius: 999px;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}

.row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 42px;
  padding: 7px 8px;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.row-label {
  color: #7b8087;
  font-size: 12px;
  line-height: 1.1;
}

.row-value {
  font-size: 14px;
  line-height: 1.25;
  color: #333;
  font-weight: 500;
  word-break: break-word;
}

.copy-row {
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 6px;
  transition: background 0.15s, border-color 0.15s;
}

.copy-row:hover {
  background: #eef4ff;
  border-color: #bfdbfe;
}

.copy-row:hover .row-value {
  color: #1d4ed8;
}

.empty {
  color: #999;
  font-size: 14px;
}

.points {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.point-card {
  border: 0;
  border-radius: 0;
  padding: 10px;
  background: var(--details-card-bg, #e9edf3);
}

.point-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.point-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}

.point-loading {
  background: #007bff;
}

.point-unloading {
  background: #dc3545;
}

.point-city {
  font-size: 15px;
  font-weight: 600;
}

.point-op {
  font-weight: 400;
  color: #888;
  font-size: 13px;
}

.pt-grid {
  padding-left: 30px;
}

.point-card {
  border-left: 3px solid transparent;
}

.point-card-top {
  border-left-color: #d4a017;
}

.point-card-key {
  border-left-color: #007bff;
}

.point-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
}

.point-badge-top {
  background: #fff3cd;
  color: #856404;
}

.point-badge-key {
  background: #cce5ff;
  color: #004085;
}

.point-address {
  font-size: 12px;
  color: #667;
  padding: 0 0 4px 30px;
  margin-top: -2px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-card {
  border: 0;
  border-radius: 0;
  padding: 8px 10px;
  background: var(--details-card-bg, #e9edf3);
}

@media (max-width: 1600px) {
  .grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) {
  .grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

.history-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
}

.comment-text {
  white-space: pre-wrap;
  color: #555;
}
</style>
